import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

interface AIAgentOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Updated AIAgent interface for the table
interface AIAgent {
    id: string;
    projectId: string;
    agentName: string;
    region: string;
    status: 'pending' | 'active' | 'rejected' | string;
    environment?: string;
    imageUrl?: string;
    gpu: string | number;
    applicationName: string;
    modelName: string;
    cloudProvider: string; // New field
    updatedAt?: string;
    createdAt?: string;
    repoUrl?: string;
    aIAgentOwner?: AIAgentOwnerInfo | null;
    reasonForRejection?: string;
}

interface AIAgentsTableProps {
    agents: AIAgent[];
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected';
}

const AIAgentsTable: React.FC<AIAgentsTableProps> = ({
    agents,
    columnFilters,
    statusType,
}) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    
    const handleRowClick = (agentId: string) => {
        navigate(`/aisolutions/aiagent/details/${agentId}`);
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
            if (!dateValue) return null;
            const numericDate = Number(dateValue);
            if (isNaN(numericDate)) return null;
            return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
        };

    const headers = [
        { key: "agentName", label: "AGENT NAME", xs: 2 },
        { key: "projectId", label: "CLOUD IDENTIFER", xs: 1.5 },
        { key: "cloudProvider", label: "CLOUD PROVIDER", xs: 1.5 },
        { key: "applicationName", label: "APPLICATION NAME", xs: 1.5 },
        { key: "gpu", label: "GPU", xs: 1 },
        { key: "modelName", label: "MODEL NAME", xs: 1.5 },
        { key: "aIAgentOwner", label: "RESOURCE OWNER", xs: 1.5 },
        { key: "createdAt", label: "ADDED ON", xs: 1.5 },
    ];

    const iconColor = statusType === "active" ? "#12B76A" : statusType === "rejected" ? "#F04438" : "#FDB022";
    const listItemBgColor = statusType === "active" ? "#FFFFFF" : statusType === "rejected" ? "#F0443810" : "#FDB02210";


    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            {/* Table Header */}
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {/* Table Body - Agent Rows */}
            {agents.map((agent, index) => (
                <ListItem
                    key={agent.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        width: '100%',
                        backgroundColor: listItemBgColor,
                        '&:hover': {
                            borderColor: "black",
                        }
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                    onClick={() => handleRowClick(agent.id)}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.agentName && (
                                <Grid item xs={headers.find(h => h.key === 'agentName')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon sx={{ color: iconColor, bgcolor: iconColor, width: "4px", height: "22px", marginRight: "8px" }} />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {agent.agentName || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.projectId && (
                                <Grid item xs={headers.find(h => h.key === 'projectId')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {agent.projectId || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                             {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" sx={{ textTransform: 'uppercase' }}>
                                        {agent.cloudProvider || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.applicationName && (
                                <Grid item xs={headers.find(h => h.key === 'applicationName')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {agent.applicationName || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.gpu && (
                                <Grid item xs={headers.find(h => h.key === 'gpu')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {String(agent.gpu ?? "0")}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.modelName && (
                                <Grid item xs={headers.find(h => h.key === 'modelName')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {agent.modelName || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.aIAgentOwner && (
                                <Grid item xs={headers.find(h => h.key === 'aIAgentOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                            src={agent.aIAgentOwner?.googleProfilePicture || undefined}
                                            alt={agent.aIAgentOwner?.name || ""}
                                            sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: agent.aIAgentOwner?.googleProfilePicture ? 'transparent' : 'rgba(0,0,0,0.1)' }}
                                        >
                                            {agent.aIAgentOwner && !agent.aIAgentOwner.googleProfilePicture ? agent.aIAgentOwner.name.charAt(0).toUpperCase() : null}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {agent.aIAgentOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(Number(agent.createdAt))}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default AIAgentsTable;